import {
  useErrorHandlingMutation,
  useErrorHandlingQuery,
} from '@backoffice-frontend/data-fetching'
import { useMoiaSuccessNotification } from '@backoffice-frontend/patterns'
import type { useBackofficeLoggedInUser } from '@backoffice-frontend/user-management-ui'
import { useRefreshPermissions } from '@backoffice-frontend/user-management-ui'
import {
  ActionItemGroup,
  ActionItemSingleSelect,
  Button,
  Callout,
  LoadingIndicator,
  SelectionMenu,
  type useSelectionMenu,
} from '@moia-dev/pace-web'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useAccountBadgeAdminPanelGroupSwitcherQuery,
  useSwitchAdminPanelGroupMutation,
} from './AccountBadgeAdminPanelGroupSwitcher.hook'

type AccountBadgeAdminPanelGroupSwitcherProps = {
  selectionMenuProps: ReturnType<typeof useSelectionMenu>
  user: NonNullable<ReturnType<typeof useBackofficeLoggedInUser>['user']>
}

export const AccountBadgeAdminPanelGroupSwitcher = ({
  selectionMenuProps,
  user,
}: AccountBadgeAdminPanelGroupSwitcherProps) => {
  const { t } = useTranslation()
  const refreshPermissions = useRefreshPermissions()
  const [query, setQuery] = useState('')
  const currentGroup = user.adminPanelTimedGroups?.[0]?.groupName ?? null
  const [selected, setSelected] = useState<string | null>(currentGroup)
  const [switchGroup] = useErrorHandlingMutation(
    useSwitchAdminPanelGroupMutation,
  )
  const { data, loading } = useErrorHandlingQuery(
    useAccountBadgeAdminPanelGroupSwitcherQuery,
    { variables: {} },
  )
  const { enqueueMoiaSuccessNotification } = useMoiaSuccessNotification()

  const handleSubmit = async () => {
    if (!selected) return
    await switchGroup({
      variables: { input: { groupName: selected, expires: null } },
    })
    enqueueMoiaSuccessNotification(
      t('authenticatedAppView_adminPanelGroupSwitchedSuccess'),
    )
    await refreshPermissions({ skipSuccessToast: true })
  }

  const filteredGroups = (data?.groups ?? [])
    .filter(group => group.assignedOrganizations?.includes(user.organizationId))
    .filter(option =>
      query.length > 0
        ? option.displayName.toLowerCase().includes(query.toLowerCase()) ||
          option.name.toLowerCase().includes(query.toLowerCase())
        : true,
    )

  const currentlyAssignedGroup = filteredGroups.filter(
    option => option.name === currentGroup,
  )
  const otherGroups = filteredGroups.filter(
    option => option.name !== currentGroup,
  )

  return (
    <SelectionMenu
      {...selectionMenuProps}
      title={t('authenticatedAppView_adminPanelGroupSwitcher_header')}
      caption={t('authenticatedAppView_adminPanelGroupSwitcher_caption')}
      footer={
        <div
          css={theme => ({
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1),
          })}
        >
          <div css={{ width: 340 }}>
            <Callout hideDismiss>
              {t('authenticatedAppView_adminPanelGroupSwitcher_callout')}
            </Callout>
          </div>

          <Button
            label={t('common_apply_label')}
            onClick={handleSubmit}
            size="compact"
            disabled={!selected}
          />
        </div>
      }
      query={query}
      onQueryChange={setQuery}
    >
      {loading && (
        <div css={theme => ({ padding: theme.spacing(1), width: 330 })}>
          <LoadingIndicator />
        </div>
      )}
      {!loading && filteredGroups.length === 0 && (
        <div css={theme => ({ padding: theme.spacing(1), width: 330 })}>
          {t('common_noResults_title')}
        </div>
      )}
      {currentlyAssignedGroup.length > 0 && (
        <ActionItemGroup
          label={t(
            'authenticatedAppView_adminPanelGroupSwitcherCurrentGroup_label',
          )}
        >
          {currentlyAssignedGroup.map(option => (
            <ActionItemSingleSelect
              style={{ width: 330 }}
              key={option.name}
              selected={selected === option.name}
              onClick={() => setSelected(option.name)}
              label={option.displayName}
            />
          ))}
        </ActionItemGroup>
      )}
      {otherGroups.length > 0 && (
        <ActionItemGroup
          label={t('authenticatedAppView_adminPanelGroupSwitcherGroups_label')}
        >
          {otherGroups.map(option => (
            <ActionItemSingleSelect
              style={{ width: 330 }}
              key={option.name}
              selected={selected === option.name}
              onClick={() => setSelected(option.name)}
              label={option.displayName}
            />
          ))}
        </ActionItemGroup>
      )}
    </SelectionMenu>
  )
}
